import { getCurrency, DEFAULT_CURRENCY } from './currencyConfig';

/**
 * Smart currency formatting that shows decimals only when meaningful
 * @param {number} valueInDollars - Amount in dollars (already divided by 100 from cents)
 * @param {object} options - Formatting options
 * @param {string} options.mode - 'short' (default) or 'long' format
 * @param {boolean} options.showSign - Show +/- sign (default: false)
 * @param {boolean} options.forceDecimals - Always show 2 decimals (default: false)
 * @param {boolean} options.roundForDisplay - Always round to whole dollars (default: false)
 * @param {string} options.currencyCode - ISO currency code (default: from context or 'USD')
 * @param {string} options.locale - Override locale for formatting
 * @param {string} options.symbol - Override symbol for display
 * @returns {string} Formatted currency string
 */
export const formatSmartCurrency = (valueInDollars, options = {}) => {
  const {
    mode = 'short',        // 'short' | 'long'
    showSign = false,      // Show +/- sign
    forceDecimals = false, // Always show 2 decimals (for inputs/edits)
    roundForDisplay = false, // Always round to whole dollars (global preference)
    currencyCode = DEFAULT_CURRENCY, // ISO currency code
    locale: localeOverride,
    symbol: symbolOverride,
  } = options;

  // Handle invalid values
  if (valueInDollars === null || valueInDollars === undefined || isNaN(valueInDollars) || !isFinite(valueInDollars)) {
    return 'N/A';
  }

  // Get currency configuration
  const currencyConfig = getCurrency(currencyCode);
  const symbol = symbolOverride || currencyConfig.symbol;
  const locale = localeOverride || currencyConfig.locale;

  const absValue = Math.abs(valueInDollars);
  const hasCents = Math.abs(valueInDollars % 1) >= 0.01;
  const sign = valueInDollars < 0 ? '-' : (showSign && valueInDollars > 0 ? '+' : '');

  // Short format (compact: $1.5k, $2.3M)
  if (mode === 'short') {
    if (absValue >= 1000000) {
      const millions = absValue / 1000000;
      const formatted = millions.toFixed(2).replace(/\.?0+$/, '');
      return `${sign}${symbol}${formatted}M`;
    } else if (absValue >= 1000) {
      const thousands = absValue / 1000;
      const formatted = thousands.toFixed(2).replace(/\.?0+$/, '');
      return `${sign}${symbol}${formatted}k`;
    } else {
      // Apply rounding preference
      if (roundForDisplay) {
        return `${sign}${symbol}${Math.round(absValue)}`;
      }
      // Show decimals only if meaningful or forced
      if (forceDecimals || hasCents) {
        return `${sign}${symbol}${absValue.toFixed(2)}`;
      } else {
        return `${sign}${symbol}${Math.round(absValue)}`;
      }
    }
  }

  // Long format (full: $1,500.00)
  // Use Intl.NumberFormat for locale-aware formatting
  try {
    const formatOptions = {
      minimumFractionDigits: (roundForDisplay || (!forceDecimals && !hasCents)) ? 0 : 2,
      maximumFractionDigits: (roundForDisplay || (!forceDecimals && !hasCents)) ? 0 : 2,
    };

    const displayValue = roundForDisplay ? Math.round(absValue) : absValue;
    const formatted = new Intl.NumberFormat(locale, formatOptions).format(displayValue);

    return `${sign}${symbol}${formatted}`;
  } catch (error) {
    // Fallback to basic formatting if Intl fails
    console.warn('Intl.NumberFormat failed, using fallback:', error);
    if (roundForDisplay) {
      return `${sign}${symbol}${Math.round(absValue).toLocaleString()}`;
    }
    if (forceDecimals || hasCents) {
      return `${sign}${symbol}${absValue.toFixed(2)}`;
    }
    return `${sign}${symbol}${Math.round(absValue).toLocaleString()}`;
  }
};

/**
 * Format a value using Intl.NumberFormat with currency style
 * This is useful for places where we want full Intl formatting with currency symbol
 * @param {number} value - Amount in dollars
 * @param {string} currencyCode - ISO currency code
 * @param {object} options - Additional Intl.NumberFormat options
 * @returns {string} Formatted currency string
 */
export const formatCurrencyIntl = (value, currencyCode = DEFAULT_CURRENCY, options = {}) => {
  const currencyConfig = getCurrency(currencyCode);

  try {
    return new Intl.NumberFormat(currencyConfig.locale, {
      style: 'currency',
      currency: currencyCode,
      ...options,
    }).format(value);
  } catch (error) {
    console.warn('Intl.NumberFormat currency formatting failed:', error);
    return `${currencyConfig.symbol}${value.toFixed(2)}`;
  }
};

/**
 * Get just the symbol for a currency code
 * @param {string} currencyCode - ISO currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = DEFAULT_CURRENCY) => {
  return getCurrency(currencyCode).symbol;
};
